import randomColor from 'randomcolor';
import {useCallback, useEffect, useState} from 'react';
import {mergeAll} from 'rxjs/operators';
import useSocket from '../../../hooks/use-socket';
import useStore from '../../../hooks/use-store';
import useReportActive from './useReportActive';

const colors = {};

export const applyTraceRemoval = (map, deviceIds) => {
    if (!Array.isArray(deviceIds)) return;
    deviceIds.forEach((id) => map.removeFeaturesTrace(id));
};

let unsubscribe$ = null;

// Posicao em transito de um device revogado ao vivo (MON-570 removeu do deviceMap antes
// dessa posicao chegar pelo socket) nao pode virar exception -- so nao ha o que traçar.
export const syncTraceOnPosition = (deviceMap, deviceId, map, doBuffer, hasDeviceSelected) => {
    const device = deviceMap.get(+deviceId);
    if (!device) return;
    if (hasDeviceSelected(device.deviceId)) return;
    if (device.withTrace) {
        const buffer = doBuffer(device.positionBuffer.toJSON());
        map.updateTrace(deviceId, buffer);
    }
};

const useMapTrace = (state, dispatch, instance) => {
    const [trace, setTrace] = useState(false);
    const {positions: positionsSocket, devicesRemoved: devicesRemovedObservable} = useSocket();
    const {instance: map, deviceMap} = state;
    const {store} = useStore();
    const {events, devices} = store;
    const reportActive = useReportActive();

    const doBuffer = useCallback(
        (positions) =>
            positions.map(({longitude, latitude, course}) => [
                longitude,
                latitude,
                course,
            ]),
        [],
    );

    const toogleTrace = useCallback(
        (device) => {
            const status = !device.withTrace;
            device.setTrace(status);
            if (status) {
                map.addFeatureTrace(
                    device.id,
                    doBuffer(device.positionBuffer.toJSON()),
                    colors[+device.id],
                );
            } else {
                map.removeFeaturesTrace(+device.id);
            }
        },
        [map],
    );

    const hasDeviceSelected = (deviceId) => {
        if (events.currentDevice || devices.selectedDevice) {
            const device = events.currentDevice || devices.selectedDevice;
            if (device.deviceId !== deviceId) {
                return true;
            }
        }
        return false;
    };

    useEffect(() => {
        if (deviceMap) {
            if (trace) {
                for (const [deviceId, device] of deviceMap) {
                    if (hasDeviceSelected(deviceId)) {
                        // what should we do?
                        return;
                    }
                    if (device.withTrace) continue;
                    const buffer = doBuffer(device.positionBuffer.toJSON());
                    map.addFeatureTrace(deviceId, buffer, colors[deviceId]);
                    device.setTrace(true);
                }
            } else {
                if (unsubscribe$) {
                    // unsubscribe$.unsubscribe();
                    for (const device of deviceMap.values()) {
                        if (hasDeviceSelected(device.deviceId)) {
                            return;
                        }
                        if (!device.withTrace) continue;
                        device.setTrace(false);
                    }
                    map.removeFeaturesTrace();
                }
            }
        }
    }, [trace, deviceMap]);

    useEffect(() => {
        if (map && deviceMap) {
            map.initTraceLayer();

            const c = randomColor({
                luminosity: 'dark',
                count: deviceMap.size,
            });

            for (const deviceId of deviceMap.keys()) {
                colors[deviceId] = c.pop();
            }

            // A camada recem-criada esta vazia: quem continua com a trilha ligada e redesenhado nela.
            for (const [deviceId, device] of deviceMap) {
                if (device.withTrace && !hasDeviceSelected(deviceId)) {
                    map.addFeatureTrace(deviceId, doBuffer(device.positionBuffer.toJSON()), colors[deviceId]);
                }
            }

            unsubscribe$ = positionsSocket
                .pipe(mergeAll())
                .subscribe(({deviceId}) => {
                    if (reportActive) return;
                    syncTraceOnPosition(deviceMap, deviceId, map, doBuffer, hasDeviceSelected);
                });
        }

        // Cleanup no MESMO efeito que atribui unsubscribe$: React roda isso antes do proximo
        // [map, deviceMap] mudar e reatribuir, entao cada troca de deviceMap desinscreve a
        // subscription da troca anterior -- nao so a ultima, no unmount. Mesmo padrao ja correto
        // em useMapTraceV2.js.
        return () => {
            unsubscribe$ && unsubscribe$.unsubscribe();
        };
    }, [map, deviceMap, reportActive]);

    // Um relatorio aberto no mapa isola o trajeto: a trilha ao vivo (device.withTrace persiste entre telas) fica
    // escondida enquanto ele estiver aberto e reaparece quando fecha. Esconder a camada, e nao apagar as feicoes,
    // mantem cada trilha na camada para a sincronizacao por posicao continuar desenhando nela. Depende de deviceMap
    // porque o efeito acima recria a camada (e redesenha a trilha ligada) quando ele muda.
    useEffect(() => {
        if (map) {
            map.setTraceLayerVisible(!reportActive);
        }
    }, [reportActive, map, deviceMap]);

    useEffect(() => {
        let devicesRemovedSubscription$;

        if (map) {
            devicesRemovedSubscription$ = devicesRemovedObservable.subscribe((deviceIds) => {
                applyTraceRemoval(map, deviceIds);
            });
        }

        return () => {
            if (devicesRemovedSubscription$) {
                devicesRemovedSubscription$.unsubscribe();
            }
        };
    }, [map, devicesRemovedObservable]);

    return {trace, setTrace, toogleTrace};
};

export default useMapTrace;
